/**
 * Shared pure functions for catalog matching and narrative parsing.
 * Extracted from suggest.tsx and requests.$id.tsx so they can be
 * imported, reused, and tested independently.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export type PieceRef = {
  id: string;
  type: "jewelry" | "clothing" | "accessory";
  name: string;
  photo_url: string | null;
  photo_url_no_bg: string | null;
  prefer_clean_bg: boolean | null;
  clean_bg_color: string | null;
};

export type CatalogEntry = PieceRef & {
  considering: boolean;
};


export type ParsedNarrative = {
  recommendation: string;
  complement: string;
  searchTerms: string;
  findThese: string;
};

// ─── Constants ───────────────────────────────────────────────────────────────

export const STOPWORDS = new Set([
  "a","an","the","and","or","of","in","on","with","for","to","from","at",
  "by","as","is","it","this","that","these","those","my","your","our",
  "pair","piece","set","style","item","one",
]);

// ─── fuzzyMatchPiece ─────────────────────────────────────────────────────────

/**
 * Fuzzy-matches a piece name from an AI response against a catalog of items.
 * Returns the best match above the 0.4 threshold, or null.
 */
export function fuzzyMatchPiece<T extends { id: string; name: string }>(
  named: string,
  candidates: T[]
): T | null {
  if (!named || candidates.length === 0) return null;
  const norm = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9 ]/g, "").trim();
  const target = norm(named);
  let best: T | null = null;
  let bestScore = 0;
  for (const c of candidates) {
    const cn = norm(c.name);
    if (cn === target) return c; // exact match
    const targetWords = target.split(" ").filter((w) => w.length > 2);
    const matches = targetWords.filter((w) => cn.includes(w)).length;
    const score = targetWords.length > 0 ? matches / targetWords.length : 0;
    if (score > bestScore && score >= 0.4) {
      bestScore = score;
      best = c;
    }
  }
  return best;
}

// ─── normalize / tokenize ────────────────────────────────────────────────────

export function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function tokenize(s: string): string[] {
  return normalize(s)
    .split(/[\s-]+/)
    .filter((t) => t.length > 2 && !STOPWORDS.has(t));
}

// ─── findCatalogMatch ────────────────────────────────────────────────────────

/**
 * Matches a gap description from the AI narrative against owned/considering
 * collection items. Returns the best match at ≥0.7 confidence, or null.
 */
export function findCatalogMatch(
  text: string,
  catalog: CatalogEntry[]
): CatalogEntry | null {
  if (!text || catalog.length === 0) return null;
  const gapNorm = normalize(text);
  const gapTokens = new Set(tokenize(text));
  if (gapTokens.size === 0) return null;
  let best: { entry: CatalogEntry; score: number } | null = null;
  for (const entry of catalog) {
    const nameNorm = normalize(entry.name);
    if (!nameNorm) continue;
    const nameTokens = tokenize(entry.name);
    if (nameTokens.length === 0) continue;
    let score = 0;
    if (nameNorm === gapNorm) score = 1;
    else if (gapNorm.includes(nameNorm) || nameNorm.includes(gapNorm)) {
      score = 0.95;
    } else {
      const overlap = nameTokens.filter((t) => gapTokens.has(t)).length;
      const ratio = overlap / nameTokens.length;
      if (ratio >= 0.6 && overlap >= 2) score = 0.5 + ratio * 0.4;
    }
    if (score >= 0.7 && (!best || score > best.score)) {
      best = { entry, score };
    }
  }
  return best?.entry ?? null;
}

// ─── cleanItemName ───────────────────────────────────────────────────────────

/**
 * Strips Eye scan description suffixes (e.g. " — Brand · USD XX.XX")
 * and caps display length at 40 characters.
 */
export function cleanItemName(name: string): string {
  const dashIdx = name.indexOf(" — ");
  const cleaned = dashIdx !== -1 ? name.slice(0, dashIdx).trim() : name;
  return cleaned.length > 40 ? cleaned.slice(0, 40).trimEnd() + "…" : cleaned;
}

// ─── parseNarrative ──────────────────────────────────────────────────────────

/**
 * Parses a Claude style narrative into its named sections.
 * Only splits on known section headers — inline bold emphasis is preserved.
 */
export function parseNarrative(raw: string): ParsedNarrative {
  const HEADER_RE =
    /(?:^|\n)\s*\*\*\s*(The Recommendation|The Feeling|One Last Thing|Find These|Pieces Selected|Complement|Search Terms)\s*\*\*\s*\n?/gi;
  const out: Record<string, string> = {};
  const matches: Array<{ title: string; start: number; end: number }> = [];
  let m: RegExpExecArray | null;
  while ((m = HEADER_RE.exec(raw)) !== null) {
    matches.push({ title: m[1].toLowerCase(), start: m.index, end: m.index + m[0].length });
  }
  const stripInline = (s: string) => s.replace(/\*+([^*]+)\*+/g, "$1").trim();
  if (matches.length === 0) {
    out.recommendation = stripInline(raw);
  } else {
    const preface = raw.slice(0, matches[0].start).trim();
    if (preface) out.recommendation = stripInline(preface);
    for (let i = 0; i < matches.length; i++) {
      const title = matches[i].title;
      const bodyEnd = i + 1 < matches.length ? matches[i + 1].start : raw.length;
      const body = stripInline(raw.slice(matches[i].end, bodyEnd));
      if (/recommend/.test(title)) out.recommendation = body;
      else if (/feeling/.test(title)) out.complement = body;
      else if (/one last/.test(title)) out.searchTerms = body;
      else if (/complement/.test(title)) out.complement = body;
      else if (/search/.test(title)) out.searchTerms = body;
      else if (/find these/.test(title)) out.findThese = body;
    }
  }
  return {
    recommendation: out.recommendation ?? "",
    complement: out.complement ?? "",
    searchTerms: out.searchTerms ?? "",
    findThese: out.findThese ?? "",
  };
}

// ─── Original style-matching exports (kept for backward compatibility) ─────────

import type { ClothingItem, JewelryItem } from "@/lib/supabase";
// JewelryItem and ClothingItem are re-exported implicitly via the
// ClassifiableItem shape below, which is intentionally narrower than
// either — classification only needs a small common subset of fields.

const normOriginal = (s: string | null | undefined) => (s ?? "").toLowerCase().trim();

const COOL_JEWELRY = ["pearl", "silver", "white gold", "platinum", "white", "pearl white", "colorless"];
const WARM_JEWELRY = ["gold", "yellow gold", "rose gold", "brass", "bronze", "vermeil", "gold filled", "gold vermeil"];

const COOL_CLOTHING = ["navy", "cream", "black", "gray", "blush", "white", "ivory"];
const WARM_CLOTHING = ["camel", "ivory", "burgundy", "olive", "brown", "beige", "taupe", "red", "orange", "yellow"];

const STATEMENT_JEWELRY = ["statement piece", "cuff", "brooch", "body chain"];

const FORMAL_CLOTHING = ["gown", "dress", "maxi dress", "midi dress", "blazer", "suit", "jumpsuit"];
const CASUAL_CLOTHING = ["t-shirt", "tank top", "jeans", "shorts", "activewear", "sweater", "cardigan"];

export const LOW_NECKLINES = ["v-neck", "scoop", "plunge", "square"];
export const HIGH_NECKLINES = ["crew", "turtleneck", "cowl"];
export const BARE_NECKLINES = ["strapless", "off shoulder", "one shoulder", "halter"];

function jewelryTone(j: Pick<JewelryItem, "color" | "material">): "cool" | "warm" | "neutral" {
  const tokens = `${normOriginal(j.color)} ${normOriginal(j.material)}`;
  if (WARM_JEWELRY.some((w) => tokens.includes(w))) return "warm";
  if (COOL_JEWELRY.some((c) => tokens.includes(c))) return "cool";
  return "neutral";
}

function isStatementJewelry(j: Pick<JewelryItem, "category">): boolean {
  return STATEMENT_JEWELRY.includes(normOriginal(j.category));
}

export type Match<T> = { item: T; score: number };

/** Rank clothing pieces from the user's wardrobe to pair with a jewelry item. */
export function matchClothingForJewelry(
  jewelry: JewelryItem,
  wardrobe: ClothingItem[],
  limit = 4,
): { matches: ClothingItem[]; rationale: string } {
  const tone = jewelryTone(jewelry);
  const statement = isStatementJewelry(jewelry);

  const scored: Match<ClothingItem>[] = wardrobe.map((c) => {
    let score = 0;
    const color = normOriginal(c.primary_color);
    const pattern = normOriginal(c.pattern);

    if (tone === "cool" && COOL_CLOTHING.includes(color)) score += 3;
    if (tone === "warm" && WARM_CLOTHING.includes(color)) score += 3;
    if (tone === "neutral") score += 1;

    if (statement && (pattern === "solid" || pattern === "")) score += 2;
    if (statement && pattern && pattern !== "solid") score -= 1;

    return { item: c, score };
  });

  scored.sort((a, b) => b.score - a.score);
  const matches = scored.slice(0, limit).map((m) => m.item);

  let rationale = "These pieces echo the same palette as this jewel.";
  if (tone === "cool") rationale = "Cool-toned pieces let pearl and silver sing against neutrals.";
  if (tone === "warm") rationale = "Warm gold loves camel, ivory and earthy tones.";
  if (statement) rationale = "Statement jewelry is happiest beside clean, solid silhouettes.";

  return { matches, rationale };
}

/** Rank jewelry pieces to pair with a clothing item. */
export function matchJewelryForClothing(
  clothing: ClothingItem,
  jewelry: JewelryItem[],
  limit = 4,
): { matches: JewelryItem[]; rationale: string } {
  const neckline = normOriginal(clothing.neckline);
  const type = normOriginal(clothing.clothing_type);
  const color = normOriginal(clothing.primary_color);
  const isFormal = FORMAL_CLOTHING.includes(type);
  const isCasual = CASUAL_CLOTHING.includes(type);
  const tone: "cool" | "warm" | "neutral" = COOL_CLOTHING.includes(color)
    ? "cool"
    : WARM_CLOTHING.includes(color)
      ? "warm"
      : "neutral";

  const scored: Match<JewelryItem>[] = jewelry.map((j) => {
    let score = 0;
    const cat = normOriginal(j.category);
    const jTone = jewelryTone(j);

    if (LOW_NECKLINES.includes(neckline) && (cat === "pendant" || cat === "necklace" || cat === "earrings"))
      score += 3;
    if (HIGH_NECKLINES.includes(neckline) && (cat === "earrings" || cat === "bracelet"))
      score += 3;
    if (BARE_NECKLINES.includes(neckline) && (cat === "necklace" || cat === "statement piece" || cat === "earrings"))
      score += 3;

    if (isFormal && (cat === "necklace" || cat === "earrings" || cat === "bracelet")) score += 2;
    if (isCasual && !STATEMENT_JEWELRY.includes(cat)) score += 1;
    if (isCasual && STATEMENT_JEWELRY.includes(cat)) score -= 1;

    if (tone === "cool" && jTone === "cool") score += 2;
    if (tone === "warm" && jTone === "warm") score += 2;
    if (tone === "neutral") score += 1;

    return { item: j, score };
  });

  scored.sort((a, b) => b.score - a.score);
  const matches = scored.slice(0, limit).map((m) => m.item);

  let rationale = "These jewels harmonize with the color and cut of this piece.";
  if (LOW_NECKLINES.includes(neckline)) rationale = "Open necklines invite a pendant or a slow drop earring.";
  else if (HIGH_NECKLINES.includes(neckline)) rationale = "High necklines pair best with earrings and a slim bracelet.";
  else if (BARE_NECKLINES.includes(neckline)) rationale = "A bare shoulder line wears a statement necklace beautifully.";
  else if (isFormal) rationale = "Formal silhouettes deserve fine, considered jewelry.";
  else if (isCasual) rationale = "Delicate, quiet pieces feel right with everyday cloth.";

  return { matches, rationale };
}

/** Categories the user does NOT yet own — surfaced as 'Complete the Look'. */
export function missingCategories(
  owned: Set<string>,
  all: readonly string[],
  limit = 3,
): string[] {
  const lowerOwned = new Set(Array.from(owned).map((s) => s.toLowerCase()));
  return all.filter((c) => !lowerOwned.has(c.toLowerCase())).slice(0, limit);
}

/** Generate a few Google-search pill labels based on the item. */
export function suggestedSearchesForJewelry(j: JewelryItem): string[] {
  const tone = jewelryTone(j);
  const cat = (j.category ?? "jewelry").toLowerCase();
  const colorWord = colorPhrase(j.color) || (tone === "warm" ? "gold" : tone === "cool" ? "silver" : "");
  const piece = [colorWord, cat].filter(Boolean).join(" ").trim() || "jewelry";
  const out: string[] = [];
  if (tone === "cool") {
    out.push(`cream silk blouse to wear with ${piece}`);
    out.push(`outfits to style a ${piece}`);
  } else if (tone === "warm") {
    out.push(`camel cashmere sweater to wear with ${piece}`);
    out.push(`outfits to style a ${piece}`);
  } else {
    out.push(`elegant outfits to wear with a ${piece}`);
    out.push(`how to style a ${piece}`);
  }
  out.push(`${piece} styling inspiration`);
  return out.slice(0, 3);
}

export function suggestedSearchesForClothing(c: ClothingItem): string[] {
  const neckline = normOriginal(c.neckline);
  const piece = [colorPhrase(c.primary_color), (c.clothing_type ?? "outfit").toLowerCase()]
    .filter(Boolean)
    .join(" ")
    .trim() || "outfit";
  const out: string[] = [];
  if (LOW_NECKLINES.includes(neckline)) {
    out.push(`pearl drop earrings to wear with a ${piece}`);
    out.push(`gold pendant necklace for a ${piece}`);
  } else if (HIGH_NECKLINES.includes(neckline)) {
    out.push(`statement earrings for a ${piece}`);
    out.push(`delicate bracelet stack with a ${piece}`);
  } else if (BARE_NECKLINES.includes(neckline)) {
    out.push(`statement necklace for a ${piece}`);
    out.push(`chandelier earrings with a ${piece}`);
  } else {
    out.push(`jewelry to style with a ${piece}`);
    out.push(`elegant accessories for a ${piece}`);
  }
  out.push(`how to accessorize a ${piece}`);
  return out.slice(0, 3);
}

export function googleSearchUrl(query: string): string {
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}

/* ---------- Natural "Complete the Look" search phrases ---------- */

export type CompleteTheLookContext = {
  /** The kind of piece the user is viewing. */
  sourceKind: "jewelry" | "clothing" | "accessory";
  /** Category of the current piece, e.g. "Bracelet", "Blouse", "Handbag". */
  sourceCategory?: string | null;
  color?: string | null;
  material?: string | null;
  pattern?: string | null;
  occasion?: string | null;
};

const FABRIC_HINT_BY_CLOTHING: Record<string, string> = {
  blouse: "silk",
  shirt: "cotton",
  "t-shirt": "cotton",
  "tank top": "silk",
  dress: "silk",
  "maxi dress": "flowing",
  "midi dress": "silk",
  "mini dress": "silk",
  skirt: "silk",
  trousers: "tailored",
  jeans: "denim",
  jacket: "tailored",
  blazer: "tailored",
  coat: "wool",
  cardigan: "cashmere",
  sweater: "cashmere",
  gown: "evening",
  jumpsuit: "tailored",
};

function colorPhrase(color?: string | null): string {
  const c = (color ?? "").toLowerCase().trim();
  if (!c) return "";
  return c.replace(/\s+/g, " ");
}

function occasionPhrase(occasion?: string | null): string {
  const o = (occasion ?? "").toLowerCase().trim();
  if (!o) return "";
  if (o === "everyday" || o === "casual") return "everyday";
  if (o === "formal" || o === "black tie") return "formal";
  if (o === "evening" || o === "cocktail") return "evening";
  if (o === "work" || o === "office") return "workwear";
  return o;
}

/** Build a natural, stylist-style Google query for a missing category card. */
export function completeTheLookQuery(
  missingCategory: string,
  ctx: CompleteTheLookContext,
): string {
  const cat = missingCategory.toLowerCase();
  const color = colorPhrase(ctx.color);
  const material = (ctx.material ?? "").toLowerCase().trim();
  const occasion = occasionPhrase(ctx.occasion);
  const sourceCat = (ctx.sourceCategory ?? "").toLowerCase().trim();

  const fabricHint = FABRIC_HINT_BY_CLOTHING[cat];
  const missingPiece = [color, fabricHint, cat].filter(Boolean).join(" ");

  let pairing = "";
  if (ctx.sourceKind === "jewelry") {
    const tone = material || color;
    pairing = sourceCat ? `${tone} ${sourceCat}`.trim() : `${tone} jewelry`.trim();
  } else if (ctx.sourceKind === "accessory") {
    pairing = sourceCat ? `${color} ${sourceCat}`.trim() : `${color} accessory`.trim();
  } else {
    pairing = sourceCat ? `${color} ${sourceCat}`.trim() : `${color} outfit`.trim();
  }
  pairing = pairing.replace(/\s+/g, " ").trim();

  const tail = occasion ? `${occasion} styling` : "styling";
  const phrase = `${missingPiece} ${pairing ? `for ${pairing} ` : ""}${tail}`;
  return phrase.replace(/\s+/g, " ").trim();
}

// ─── Wardrobe Engagement Segments ──────────────────────────────────────────
//
// Six usage-based segments plus an "Unclassified" state for items too new
// to have any history. This is computed fresh every time from current
// data — never stored as a column — so an item's segment is always
// current and naturally moves as she favorites it, saves it into a look,
// or it gets pulled into a generated Style Me result. No reclassification
// step is ever needed; fluidity is a property of computing live, not a
// feature to maintain separately.
//
// has_feelings is intentionally NOT part of the base segment definition.
// It is a second, independent dimension layered on top — an item's
// emotional flag and its usage-based segment are separate facts, since
// stated emotional attachment is at least as predictive of future use as
// usage history, and conflating the two loses information either could
// carry alone.

export type EngagementSegment =
  | "Core"
  | "Loved but underused"
  | "Workhorse"
  | "Aspirational"
  | "Dormant Opportunity"
  | "Orphan"
  | "Unclassified";

export type ClassifiableItem = {
  id: string;
  is_favorite?: boolean | null;
  feelings?: string[] | null;
  occasion?: string | null;
  created_at?: string | null;
};

export type ClassificationContext = {
  /** Item IDs that appear in saved_look_items (explicitly saved looks). */
  savedLookItemIds: Set<string>;
  /** Item IDs that appear in any style_requests.selected_item_ids,
   * saved or not — broader exposure signal than savedLookItemIds. */
  selectedInAnyLookIds: Set<string>;
  /** Occasions she has actually styled for, from her style_requests
   * history (event field), lowercased for comparison. */
  actualOccasions: Set<string>;
  /** Items newer than this many days are Unclassified regardless of
   * other signals — insufficient history to judge yet. Default 14. */
  newItemWindowDays?: number;
  /** Reference "now" for age calculations — defaults to current time,
   * exposed for deterministic testing. */
  now?: Date;
};

export type ItemClassification = {
  segment: EngagementSegment;
  hasFeelings: boolean;
};

/**
 * Classifies a single item into one of the six Wardrobe Engagement
 * Segments, plus an independent has_feelings flag. Pure function —
 * same inputs always produce the same output, no side effects, no
 * persistence. Call this fresh wherever a current classification
 * is needed; do not cache the result on the item row.
 */
export function classifyEngagementSegment(
  item: ClassifiableItem,
  ctx: ClassificationContext
): ItemClassification {
  const hasFeelings = (item.feelings?.length ?? 0) > 0;
  const isFavorite = item.is_favorite === true;
  const newItemWindowDays = ctx.newItemWindowDays ?? 14;
  const now = ctx.now ?? new Date();

  const ageInDays = item.created_at
    ? (now.getTime() - new Date(item.created_at).getTime()) / (1000 * 60 * 60 * 24)
    : Infinity;

  if (ageInDays < newItemWindowDays) {
    return { segment: "Unclassified", hasFeelings };
  }

  const usedInSavedLook = ctx.savedLookItemIds.has(item.id);
  const usedInAnyLook = ctx.selectedInAnyLookIds.has(item.id);

  if (isFavorite && usedInSavedLook) {
    return { segment: "Core", hasFeelings };
  }
  if (isFavorite && !usedInSavedLook) {
    return { segment: "Loved but underused", hasFeelings };
  }
  if (!isFavorite && usedInSavedLook) {
    return { segment: "Workhorse", hasFeelings };
  }
  if (!isFavorite && !usedInSavedLook && usedInAnyLook) {
    return { segment: "Dormant Opportunity", hasFeelings };
  }

  // Neither favorited, nor saved, nor ever selected into any generated
  // look. Check occasion fit before falling to Orphan.
  const itemOccasion = (item.occasion ?? "").toLowerCase().trim();
  const occasionMatches = itemOccasion.length > 0 && ctx.actualOccasions.has(itemOccasion);

  if (!occasionMatches) {
    return { segment: "Aspirational", hasFeelings };
  }

  // Has a matching occasion but has still never been favorited, saved,
  // or selected into anything — no evidence this item combines with
  // anything else she owns.
  return { segment: "Orphan", hasFeelings };
}

// ─── Staleness-Aware Selection Scoring ───────────────────────────────────

export type StalenessAwareItem = ClassifiableItem & {
  /** Timestamp this item was last selected into AI-generated output
   * (Style Me or Daily Look). Null/undefined means never surfaced. */
  last_surfaced_at?: string | null;
};

export const FAVORITE_WEIGHT = 3;
export const FEELINGS_WEIGHT = 3;
export const RECENCY_ADDED_WEIGHT = 1;
export const STALENESS_WEIGHT = 2;
export const STALENESS_DECAY_WINDOW_DAYS = 60;
export const ARCHETYPE_WEIGHT = 2; // below FAVORITE_WEIGHT (3) — explicit signal outranks inferred

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/**
 * Rewards items that have gone unsurfaced the longest, scaled 0 to 1
 * across STALENESS_DECAY_WINDOW_DAYS. Orphan and Unclassified segments
 * always return 0 — Orphan because nothing about the item indicates it
 * combines with anything else she owns, so surfacing it would not be a
 * confident recommendation; Unclassified because there isn't enough
 * history yet to judge. Null last_surfaced_at means the item has never
 * been surfaced and is treated as maximally stale, not as missing data.
 */
export function stalenessBonus(
  segment: EngagementSegment,
  lastSurfacedAt: string | null | undefined,
  now: Date = new Date()
): number {
  if (segment === "Orphan" || segment === "Unclassified") return 0;
  if (!lastSurfacedAt) return 1;
  const daysSince =
    (now.getTime() - new Date(lastSurfacedAt).getTime()) / (1000 * 60 * 60 * 24);
  return clamp(daysSince / STALENESS_DECAY_WINDOW_DAYS, 0, 1);
}

/**
 * Rewards items added to the collection recently, independent of
 * staleness (which tracks surfacing, not acquisition — a brand-new,
 * never-surfaced item correctly scores high on both at once). Mirrors
 * stalenessBonus's decay shape, inverted, reusing the same 60-day
 * window for consistency: a brand-new item scores 1.0, decaying
 * linearly to 0 by day 60.
 */
export function recencyAddedSignal(
  createdAt: string | null | undefined,
  now: Date = new Date()
): number {
  if (!createdAt) return 0;
  const daysSince =
    (now.getTime() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24);
  return clamp(1 - daysSince / STALENESS_DECAY_WINDOW_DAYS, 0, 1);
}

export type SelectionScore = {
  total: number;
  segment: EngagementSegment;
  hasFeelings: boolean;
  components: {
    favorite: number;
    feelings: number;
    recencyAdded: number;
    staleness: number;
  };
};

/**
 * Combines classifyEngagementSegment with the staleness and recency
 * signals into a single selection score. Pure function, no side
 * effects, no persistence — classification and score are always
 * computed fresh from current data. The breakdown in `components` is
 * exposed deliberately so callers (Style Me, Daily Look) can determine
 * *why* an item scored highly — e.g. whether to surface its feelings
 * text in generated copy depends on whether staleness, not recency,
 * was the dominant reason it was chosen.
 */
// DUPLICATED in supabase/functions/_shared/engagement-scoring.ts for
// the Deno runtime (caileve-daily-look). Any change to the scoring
// logic below — classifyEngagementSegment, stalenessBonus,
// recencyAddedSignal, or scoreItemForSelection — must be mirrored
// there, or the two selection surfaces will silently diverge.
export function scoreItemForSelection(
  item: StalenessAwareItem,
  ctx: ClassificationContext
): SelectionScore {
  const { segment, hasFeelings } = classifyEngagementSegment(item, ctx);
  const now = ctx.now ?? new Date();

  const favorite = FAVORITE_WEIGHT * (item.is_favorite === true ? 1 : 0);
  const feelings = FEELINGS_WEIGHT * (hasFeelings ? 1 : 0);
  const recencyAdded = RECENCY_ADDED_WEIGHT * recencyAddedSignal(item.created_at, now);
  const staleness = STALENESS_WEIGHT * stalenessBonus(segment, item.last_surfaced_at, now);

  return {
    total: favorite + feelings + recencyAdded + staleness,
    segment,
    hasFeelings,
    components: { favorite, feelings, recencyAdded, staleness },
  };
}

// ─── Complete-Look Composition Classifiers ───────────────────────────────
//
// DUPLICATED in supabase/functions/_shared/engagement-scoring.ts for the
// Deno runtime. Any change to classifyGarmentRole, classifyAccessoryRole,
// classifyJewelryCategory, or seasonalFit must be mirrored there.

/**
 * GarmentRole classifies a clothing item's structural position in a look.
 *
 * base_top   — worn against the body as the top layer: blouse, shirt, tee,
 *              tank, bodysuit, cami, crop top, polo, henley, tunic.
 *              Requires a bottom to form a complete foundation.
 *
 * mid_layer  — worn over a base_top for warmth or style: sweater, knit,
 *              cardigan, hoodie, sweatshirt, turtleneck, fleece.
 *              Optional; does not replace the base_top requirement.
 *
 * one_piece  — self-contained body coverage: dress, gown, jumpsuit, romper,
 *              overalls, dungarees, playsuit, boilersuit, pinafore.
 *              Does not require a separate bottom.
 *
 * outerwear  — outermost layer: coat, jacket, blazer, trench, parka, anorak,
 *              cape. Requires a base beneath it — never the foundation.
 *
 * bottom     — lower-body coverage: trousers, skirt, jeans, shorts,
 *              leggings, culottes, palazzo, joggers.
 *
 * other      — non-structural: scarves, belts, hats when stored as clothing,
 *              or anything unclassifiable.
 *
 * DUPLICATED in supabase/functions/_shared/engagement-scoring.ts — mirror
 * every change there. The "top" role has been intentionally retired and split
 * into base_top and mid_layer to support correct layered outfit composition.
 */
export type GarmentRole = "one_piece" | "base_top" | "mid_layer" | "outerwear" | "bottom" | "other";

// Negation guards — checked BEFORE substring keyword matching.
// These prevent compound words (e.g. "bodysuit") from matching a keyword
// substring ("suit") and landing in the wrong role.
const ONE_PIECE_NEGATIONS = ["bodysuit", "swimsuit", "wetsuit", "tracksuit"];

const ONE_PIECE_KEYWORDS = ["dress", "gown", "jumpsuit", "romper", "overalls", "dungarees", "playsuit", "boilersuit", "pinafore"];
// Note: "suit" intentionally removed — too many false positives (bodysuit, swimsuit, tracksuit).
// Pantsuit and trouser suit are matched via name fallback below.

const OUTERWEAR_KEYWORDS = ["coat", "jacket", "blazer", "trench", "parka", "anorak", "cape", "shrug"];

const BOTTOM_KEYWORDS = ["skirt", "trouser", "jean", "pant", "shorts", "legging", "culotte", "palazzo", "jogger", "sweatpant"];

// base_top: worn against the body as the top garment.
const BASE_TOP_KEYWORDS = ["blouse", "shirt", "tee", "t-shirt", "tank", "cami", "top", "crop", "bustier", "corset top", "polo", "henley", "tunic", "bodysuit", "leotard", "unitard"];

// mid_layer: worn over a base_top for warmth or texture.
const MID_LAYER_KEYWORDS = ["sweater", "knit", "knitwear", "cardigan", "hoodie", "sweatshirt", "turtleneck", "fleece", "pullover"];

const ACTIVEWEAR_BOTTOM_HINTS = ["legging", "jogger", "short", "pant", "skort"];
const ACTIVEWEAR_TOP_HINTS = ["tank", "tee", "t-shirt", "bra", "hoodie", "crop", "top"];

// Explicit role map for the fixed clothing_type values offered by the
// app's add/edit UI (CLOTHING_TYPE_PRESETS in src/components/smart-select.tsx)
// plus common synonyms and historical free-text values seen in the DB.
// This is the durable path: when clothing_type is one of the values a user
// can actually pick, we do NOT rely on keyword substring matching — a source
// of two prior regressions (bare "Top" and bare "Bottoms" both falling
// through to "other"). Keyword matching remains as a fallback for legacy
// free text and name-only classification.
const PRESET_TYPE_ROLE_MAP: Record<string, GarmentRole> = {
  // base_top
  "blouse": "base_top", "shirt": "base_top", "t-shirt": "base_top", "tshirt": "base_top", "tee": "base_top",
  "tank top": "base_top", "tank": "base_top", "top": "base_top", "tops": "base_top",
  "bodysuit": "base_top", "bustier": "base_top", "strapless": "base_top",
  "cropped shirt": "base_top", "crop top": "base_top", "camisole": "base_top", "cami": "base_top",
  "polo": "base_top", "henley": "base_top", "tunic": "base_top", "leotard": "base_top",
  // mid_layer
  "cardigan": "mid_layer", "sweater": "mid_layer", "knitwear": "mid_layer", "knit": "mid_layer",
  "hoodie": "mid_layer", "sweatshirt": "mid_layer", "turtleneck": "mid_layer", "pullover": "mid_layer",
  // outerwear
  "jacket": "outerwear", "blazer": "outerwear", "coat": "outerwear", "outerwear": "outerwear",
  "trench": "outerwear", "trench coat": "outerwear", "parka": "outerwear",
  // bottom
  "skirt": "bottom", "maxi skirt": "bottom", "midi skirt": "bottom", "mini skirt": "bottom",
  "trousers": "bottom", "trouser": "bottom", "pants": "bottom", "pant": "bottom",
  "jeans": "bottom", "jean": "bottom", "shorts": "bottom", "leggings": "bottom",
  "sweatpants": "bottom", "joggers": "bottom", "culottes": "bottom",
  "bottom": "bottom", "bottoms": "bottom", "skort": "bottom",
  // one_piece
  "dress": "one_piece", "maxi dress": "one_piece", "midi dress": "one_piece", "mini dress": "one_piece",
  "sweater dress": "one_piece", "gown": "one_piece", "jumpsuit": "one_piece", "romper": "one_piece",
  "playsuit": "one_piece", "overalls": "one_piece", "dungarees": "one_piece", "pinafore": "one_piece",
  "suit": "one_piece", "pantsuit": "one_piece", "trouser suit": "one_piece",
};

export function classifyGarmentRole(
  clothingType: string | null | undefined,
  name: string | null | undefined,
  styleAttributes?: { weather?: { layer_type?: string | null } | null } | null
): GarmentRole {
  const type = (clothingType ?? "").toLowerCase();
  const itemName = (name ?? "").toLowerCase();
  const haystack = `${type} ${itemName}`;
  const layerType = styleAttributes?.weather?.layer_type;
  const layerRole: GarmentRole | null =
    layerType === "base" ? "base_top" :
      layerType === "light_layer" || layerType === "mid_layer" ? "mid_layer" :
        layerType === "outerwear" || layerType === "heavy_coat" ? "outerwear" :
          null;

  const maybeLayerAdjusted = (role: GarmentRole): GarmentRole => {
    // weather.layer_type is a useful layering hint, but not a garment category.
    // Backfilled rows commonly mark dresses, pants, skirts, and shorts as
    // layer_type="base"; treating that as "base_top" before text/type matching
    // silently removes real bottoms and one-pieces from the daily-look gate.
    // Let strong category signals win; only use non-base layer hints to refine
    // top/layer/outerwear ambiguity.
    if ((role === "base_top" || role === "mid_layer" || role === "outerwear") && layerRole && layerRole !== "base_top") {
      return layerRole;
    }
    return role;
  };

  // ── Activewear branch ──
  if (type.includes("activewear")) {
    if (ACTIVEWEAR_BOTTOM_HINTS.some((k) => itemName.includes(k))) return "bottom";
    if (ACTIVEWEAR_TOP_HINTS.some((k) => itemName.includes(k))) return "base_top";
    // Bare "Activewear" tag with no top/bottom hint in the name. Defaulting
    // to base_top keeps the item in the daily-look foundation gate rather
    // than silently dropping it — the same class of bug as bare "Top"/"Bottoms".
    return "base_top";
  }

  // ── Explicit preset / synonym map (exact match on trimmed type) ──
  // Runs before keyword substring matching so bare category tags like
  // "Bottoms" / "Suit" / "Outerwear" never fall through to "other".
  const trimmedType = type.trim();
  if (trimmedType && Object.prototype.hasOwnProperty.call(PRESET_TYPE_ROLE_MAP, trimmedType)) {
    return maybeLayerAdjusted(PRESET_TYPE_ROLE_MAP[trimmedType]);
  }

  // ── Negation guards: check before any substring matching ──
  const isNegated = ONE_PIECE_NEGATIONS.some((n) => haystack.includes(n));

  // ── Type-level classification (clothing_type field, most reliable) ──
  if (!isNegated && ONE_PIECE_KEYWORDS.some((k) => type.includes(k))) return "one_piece";
  if (OUTERWEAR_KEYWORDS.some((k) => type.includes(k))) return maybeLayerAdjusted("outerwear");
  if (BOTTOM_KEYWORDS.some((k) => type.includes(k))) return "bottom";
  if (MID_LAYER_KEYWORDS.some((k) => type.includes(k))) return maybeLayerAdjusted("mid_layer");
  if (BASE_TOP_KEYWORDS.some((k) => type.includes(k))) return maybeLayerAdjusted("base_top");

  // ── Special case: "pantsuit" / "trouser suit" — one_piece despite negation guard ──
  if (type.includes("pantsuit") || type.includes("trouser suit")) return "one_piece";

  // ── Haystack fallback (name-based when clothing_type is null or generic) ──
  if (!isNegated && ONE_PIECE_KEYWORDS.some((k) => haystack.includes(k))) return "one_piece";
  if (OUTERWEAR_KEYWORDS.some((k) => haystack.includes(k))) return maybeLayerAdjusted("outerwear");
  if (BOTTOM_KEYWORDS.some((k) => haystack.includes(k))) return "bottom";
  if (MID_LAYER_KEYWORDS.some((k) => haystack.includes(k))) return maybeLayerAdjusted("mid_layer");
  if (BASE_TOP_KEYWORDS.some((k) => haystack.includes(k))) return maybeLayerAdjusted("base_top");

  // ── Negated items that didn't match anything else ──
  if (haystack.includes("bodysuit") || haystack.includes("leotard") || haystack.includes("unitard")) return "base_top";
  if (haystack.includes("swimsuit") || haystack.includes("wetsuit")) return "one_piece";

  if (layerRole) return layerRole;

  return "other";
}

/**
 * AccessoryRole classifies an accessory by its outfit function.
 *
 * bag              — carry item: handbag, clutch, tote, crossbody, backpack.
 * footwear         — shoes: heels, flats, boots, sandals, sneakers, mules.
 * belt             — waist definition; relevant with separates looks.
 * hat              — headwear; can be weather-driven or style accent.
 * scarf            — neck/shoulder wrap; weather-driven or decorative.
 * gloves           — hand covering; weather-driven, cold/snow conditions.
 * sunglasses       — eyewear; condition-driven (clear/sunny weather).
 * watch            — wrist timepiece; always eligible, note bracelet stacking.
 * other            — wallets, keychains, umbrellas, hair accessories, misc.
 *
 * DUPLICATED in supabase/functions/_shared/engagement-scoring.ts — mirror
 * every change there.
 */
export type AccessoryRole = "bag" | "footwear" | "belt" | "hat" | "scarf" | "gloves" | "sunglasses" | "watch" | "other";

// Includes luxury brand names that identify bag items without containing generic keywords.
const BAG_KEYWORDS = ["bag", "clutch", "tote", "crossbody", "backpack", "purse", "satchel", "hobo", "baguette", "minaudière", "birkin", "kelly", "speedy", "neverfull", "pochette", "woc", "boy bag", "le boy", "flap bag", "belt bag", "saddle bag", "bamboo bag"];
const FOOTWEAR_KEYWORDS = ["heel", "flat", "boot", "sandal", "sneaker", "mule", "loafer", "pump", "oxford", "espadrille", "slipper", "trainer", "shoe", "stiletto", "wedge", "platform", "samba", "gazelle", "stan smith", "air force", "air max", "chuck taylor", "yeezy", "vans", "converse"];
const BELT_KEYWORDS = ["belt"];
const HAT_KEYWORDS = ["hat", "beanie", "beret", "cap", "fedora", "cloche", "bucket hat", "wide brim", "turban", "headband"];
const SCARF_KEYWORDS = ["scarf", "shawl", "wrap", "stole", "bandana", "neckerchief"];
const GLOVES_KEYWORDS = ["glove", "mitten"];
const SUNGLASSES_KEYWORDS = ["sunglass", "sunglasses", "shades", "goggle"];
const WATCH_KEYWORDS = ["watch", "timepiece"];

/**
 * Classifies an accessory item by its outfit role. Checks both the
 * category/accessory_type field and the item name — deliberately
 * lenient to handle free-text entries and legacy catalog items.
 */
export function classifyAccessoryRole(
  category: string | null | undefined,
  name: string | null | undefined
): AccessoryRole {
  const cat = (category ?? "").toLowerCase();
  const itemName = (name ?? "").toLowerCase();
  const haystack = `${cat} ${itemName}`;
  if (BAG_KEYWORDS.some((k) => haystack.includes(k))) return "bag";
  if (FOOTWEAR_KEYWORDS.some((k) => haystack.includes(k))) return "footwear";
  if (BELT_KEYWORDS.some((k) => haystack.includes(k))) return "belt";
  if (HAT_KEYWORDS.some((k) => haystack.includes(k))) return "hat";
  if (SCARF_KEYWORDS.some((k) => haystack.includes(k))) return "scarf";
  if (GLOVES_KEYWORDS.some((k) => haystack.includes(k))) return "gloves";
  if (SUNGLASSES_KEYWORDS.some((k) => haystack.includes(k))) return "sunglasses";
  if (WATCH_KEYWORDS.some((k) => haystack.includes(k))) return "watch";
  return "other";
}

export type JewelryCategory = "necklace" | "earrings" | "bracelet" | "ring" | "other";

const NECKLACE_KEYWORDS = ["necklace", "pendant", "chain", "choker"];
const EARRINGS_KEYWORDS = ["earring"];
const BRACELET_KEYWORDS = ["bracelet", "cuff", "bangle"];
const RING_KEYWORDS = ["ring"];

export function classifyJewelryCategory(
  category: string | null | undefined,
  name: string | null | undefined
): JewelryCategory {
  const cat = (category ?? "").toLowerCase();
  const itemName = (name ?? "").toLowerCase();
  const haystack = `${cat} ${itemName}`;
  if (NECKLACE_KEYWORDS.some((k) => haystack.includes(k))) return "necklace";
  if (EARRINGS_KEYWORDS.some((k) => haystack.includes(k))) return "earrings";
  if (BRACELET_KEYWORDS.some((k) => haystack.includes(k))) return "bracelet";
  if (RING_KEYWORDS.some((k) => haystack.includes(k))) return "ring";
  return "other";
}

export type Season = "spring" | "summer" | "fall" | "winter";

/**
 * A soft scoring adjustment, not a filter. Returns +0.5 when the
 * item's recorded seasonality includes the current season or is
 * empty (all-season), -0.5 when it explicitly excludes the current
 * season, and 0 when there's no weather data at all — unknown is
 * treated as neutral, not penalized, since style_attributes coverage
 * is incomplete for older items and a missing-data penalty would
 * unfairly suppress exactly the well-loved older pieces this app's
 * staleness system exists to resurface.
 */
export function seasonalFit(
  styleAttributes: { weather?: { seasonality?: string[] } | null } | null | undefined,
  currentSeason: Season
): number {
  const seasonality = styleAttributes?.weather?.seasonality;
  if (!Array.isArray(seasonality) || seasonality.length === 0) return 0.5;
  return seasonality.includes(currentSeason) ? 0.5 : -0.5;
}

import type { WeatherBand, WeatherCondition, WeatherContext } from "@/lib/weather";

const EXPECTED_WARMTH: Record<WeatherBand, [number, number]> = {
  hot: [0, 1],
  warm: [0, 2],
  mild: [1, 3],
  cool: [2, 3],
  cold: [3, 4],
};

/**
 * A soft scoring adjustment using actual live weather — temperature
 * band and precipitation — rather than the coarse seasonality array
 * seasonalFit relies on. Only meaningful where live weather has been
 * fetched, which today is Style Me only. Like seasonalFit, missing
 * data is always neutral, never penalized — an item one step outside
 * the expected warmth range is also treated as neutral, since that's
 * close enough not to count against it; only a clear mismatch (a
 * heavy coat's warmth rating on a hot day, or an item explicitly
 * marked not rain-safe when it's actually raining) is penalized.
 */
export function weatherPrecisionFit(
  styleAttributes: { weather?: { warmth?: number; rain_safe?: boolean } | null } | null | undefined,
  band: WeatherBand | null | undefined,
  condition: WeatherCondition | null | undefined
): number {
  const w = styleAttributes?.weather;
  if (!w) return 0;
  let score = 0;

  if (band && typeof w.warmth === "number") {
    const [lo, hi] = EXPECTED_WARMTH[band];
    if (w.warmth >= lo && w.warmth <= hi) score += 0.3;
    else if (w.warmth < lo - 1 || w.warmth > hi + 1) score -= 0.3;
  }

  if (condition === "rain" && typeof w.rain_safe === "boolean") {
    score += w.rain_safe ? 0.2 : -0.4;
  }

  return score;
}

// ─── Clothing Occasion Eligibility ───────────────────────────────────────────
//
// A hard pre-selection filter — mirrors passesWarmthFilter in purpose.
// Returns false only for clear mismatches (a hoodie at a formal wedding,
// activewear at dinner). Returns true when there is any doubt — missing
// data is never penalized. The formalityTier parameter uses the same
// 1–4 scale as computeOccasionFormality in style-selection.ts.
//
// DUPLICATED in supabase/functions/caileve-daily-look/index.ts —
// mirror any change there.
export function passesClothingOccasionFilter(
  clothingType: string | null | undefined,
  itemOccasion: string | null | undefined,
  formalityTier: number
): boolean {
  const type = (clothingType ?? "").toLowerCase().trim();
  const occ = (itemOccasion ?? "").toLowerCase().trim();

  // At formality tier 3+ (evening, cocktail, dinner, date night):
  // exclude activewear and overtly casual categories.
  if (formalityTier >= 3) {
    const casualTypes = ["activewear", "sweatpant", "jogger", "hoodie", "sweatshirt", "t-shirt"];
    if (casualTypes.some((k) => type.includes(k))) return false;
    // If item is explicitly tagged casual/everyday and occasion is formal/evening, exclude.
    if (
      (occ === "casual" || occ === "everyday") &&
      formalityTier >= 4
    ) return false;
  }

  // At formality tier 4 (formal, wedding, gala, black tie):
  // only allow one-piece-category items and formal-appropriate separates.
  if (formalityTier >= 4) {
    const formalAllowed = [
      "dress", "gown", "jumpsuit", "romper", "suit",
      "blouse", "shirt", "skirt", "trouser", "pant", "blazer",
    ];
    if (type && !formalAllowed.some((k) => type.includes(k))) return false;
  }

  return true;
}

// ─── Statement Jewelry Detection ─────────────────────────────────────────────
//
// Used to prevent statement-on-statement jewelry conflict:
// a statement necklace paired with statement earrings competes visually.
// Checks both the category field and the item name for signal.
export function classifyIsStatementJewelry(
  category: string | null | undefined,
  name: string | null | undefined
): boolean {
  const cat = (category ?? "").toLowerCase().trim();
  const itemName = (name ?? "").toLowerCase().trim();
  const haystack = `${cat} ${itemName}`;
  // Category-level signals
  if (["statement piece", "body chain", "brooch"].some((k) => haystack.includes(k))) return true;
  // Name-level signals for earrings that are structurally statement
  if (["chandelier", "tassel", "fringe", "shoulder duster", "statement earring"].some((k) => haystack.includes(k))) return true;
  // Cuff is structural/statement when it is the primary jewelry category
  if (cat === "cuff") return true;
  return false;
}

// ─── Breathability Filter ─────────────────────────────────────────────────────
//
// A hard pre-selection filter for hot weather — mirrors passesWarmthFilter
// in purpose and pattern. Only filters on the hot band (≥28°C) where
// low-breathability items cause genuine discomfort. Warm band and below:
// no hard filter. Missing breathability data is never penalized.
//
// DUPLICATED in supabase/functions/caileve-daily-look/index.ts as
// passesBreathabilityForDaily — mirror any change there.
// ─── Selection Pure Utilities (relocated from style-selection.ts — layer cleanup) ───

export function weatherAttrFor(sa: unknown): string {
  if (!sa || typeof sa !== "object") return "";
  const w = (sa as Record<string, unknown>).weather;
  if (!w || typeof w !== "object") return "";
  const wr = w as Record<string, unknown>;
  const parts: string[] = [];
  if (typeof wr.warmth === "number") {
    const w0 = wr.warmth;
    parts.push(
      w0 <= 0 ? "cooling" :
      w0 === 1 ? "light" :
      w0 === 2 ? "moderate warmth" :
      w0 === 3 ? "warm" : "heavy",
    );
  }
  if (typeof wr.layer_type === "string") {
    const lt = wr.layer_type;
    const lw =
      lt === "base" ? "base layer" :
      lt === "light_layer" ? "light layer" :
      lt === "mid_layer" ? "mid layer" :
      lt === "outerwear" ? "outerwear" :
      lt === "heavy_coat" ? "heavy coat" : null;
    if (lw) parts.push(lw);
  }
  if (wr.rain_safe === true) parts.push("rain-safe");
  else if (wr.rain_safe === false) parts.push("not rain-safe");
  if (Array.isArray(wr.seasonality) && wr.seasonality.length > 0) {
    parts.push(
      (wr.seasonality as unknown[])
        .filter((s) => typeof s === "string")
        .join("/"),
    );
  }
  return parts.join(", ");
}

export function isStalenessSurfaced(
  s: ReturnType<typeof scoreItemForSelection> | null
): boolean {
  return !!s && s.components.staleness > 0 &&
    s.components.staleness > s.components.recencyAdded;
}

export function withFeelingNote(
  desc: string | null,
  feelings: string[] | null | undefined,
  score: ReturnType<typeof scoreItemForSelection> | null
): string | null {
  if (!desc || !score || !isStalenessSurfaced(score) ||
      !score.hasFeelings) return desc;
  const text = (feelings ?? []).join(", ");
  return text ? `${desc}, feelings: ${text}` : desc;
}

// ─── Style Intelligence: Weather + Occasion filters ──────────────────────────

export type WeatherRequirements = {
  outerwear_required: boolean;
  mid_layer_preferred: boolean;
  warmth_target: number;
  exclude_base_warmth_above: number | null;
  rain_active: boolean;
  snow_active: boolean;
  footwear_open_toe_excluded: boolean;
  weather_accessories_suggested: Array<"hat" | "scarf" | "gloves">;
  sunglasses_relevant: boolean;
};

export function computeWeatherRequirements(
  weather: WeatherContext | null
): WeatherRequirements {
  const neutral: WeatherRequirements = {
    outerwear_required: false,
    mid_layer_preferred: false,
    warmth_target: 2,
    exclude_base_warmth_above: null,
    rain_active: false,
    snow_active: false,
    footwear_open_toe_excluded: false,
    weather_accessories_suggested: [],
    sunglasses_relevant: false,
  };
  if (!weather || weather.source === "unavailable") return neutral;

  const band: WeatherBand = weather.band;
  const condition: WeatherCondition = weather.condition;
  const rain_active = condition === "rain";
  const snow_active = condition === "snow";

  const byBand: Record<WeatherBand, Partial<WeatherRequirements>> = {
    hot:  { outerwear_required: false, mid_layer_preferred: false, warmth_target: 0, exclude_base_warmth_above: 1, footwear_open_toe_excluded: false, weather_accessories_suggested: [] },
    warm: { outerwear_required: false, mid_layer_preferred: false, warmth_target: 1, exclude_base_warmth_above: 2, footwear_open_toe_excluded: false, weather_accessories_suggested: [] },
    mild: { outerwear_required: false, mid_layer_preferred: false, warmth_target: 2, exclude_base_warmth_above: null, footwear_open_toe_excluded: false, weather_accessories_suggested: [] },
    cool: { outerwear_required: true,  mid_layer_preferred: true,  warmth_target: 3, exclude_base_warmth_above: null, footwear_open_toe_excluded: true,  weather_accessories_suggested: ["scarf"] },
    cold: { outerwear_required: true,  mid_layer_preferred: true,  warmth_target: 4, exclude_base_warmth_above: null, footwear_open_toe_excluded: true,  weather_accessories_suggested: ["hat", "scarf", "gloves"] },
  };

  const base = { ...neutral, ...byBand[band] };

  if (rain_active || snow_active) {
    base.rain_active = true;
    base.footwear_open_toe_excluded = true;
    if (!base.weather_accessories_suggested.includes("scarf")) {
      base.weather_accessories_suggested = [...base.weather_accessories_suggested];
    }
  }
  if (snow_active) {
    base.snow_active = true;
    if (!base.weather_accessories_suggested.includes("hat")) {
      base.weather_accessories_suggested = ["hat", "scarf", "gloves"];
    }
  }

  base.sunglasses_relevant = condition === "clear" || condition === "clouds";

  return base;
}

export function passesWarmthFilter(
  styleAttributes: unknown,
  req: WeatherRequirements
): boolean {
  if (req.exclude_base_warmth_above === null) return true;
  const w = (styleAttributes as { weather?: { warmth?: number } } | null)?.weather?.warmth;
  if (typeof w !== "number") return true;
  return w <= req.exclude_base_warmth_above;
}

export function isRainUnsafe(styleAttributes: unknown): boolean {
  const rs = (styleAttributes as { weather?: { rain_safe?: boolean } } | null)?.weather?.rain_safe;
  return rs === false;
}

export function isOpenToeFootwear(name: string, category: string | null): boolean {
  const h = `${(category ?? "").toLowerCase()} ${name.toLowerCase()}`;
  return ["sandal", "mule", "espadrille", "open toe", "peep toe", "slide"].some((k) => h.includes(k));
}

export function computeOccasionFormality(event: string): number {
  const e = event.toLowerCase();
  if (["formal", "wedding", "gala", "black tie"].some((k) => e.includes(k))) return 4;
  if (["evening", "cocktail", "dinner", "date night", "special occasion"].some((k) => e.includes(k))) return 3;
  if (["work", "brunch", "business", "church"].some((k) => e.includes(k))) return 2;
  return 1;
}

export function passesBreathabilityFilter(
  styleAttributes: { weather?: { breathability?: number } | null } | null | undefined,
  band: string | null | undefined
): boolean {
  if (band !== "hot") return true;
  const b = styleAttributes?.weather?.breathability;
  if (typeof b !== "number") return true;
  return b >= 2;
}

// ─── Jewelry Tone Classification (exported) ───────────────────────────────────
//
// Renamed and exported version of the private jewelryTone() function.
// Takes individual color and material strings rather than a JewelryItem object
// so it can be called from style-selection.ts with pool item fields directly.
// Used by autoJewelry to prefer matching-tone earrings and third pieces after
// the primary necklace pick.
export function classifyJewelryTone(
  color: string | null | undefined,
  material: string | null | undefined
): "warm" | "cool" | "neutral" {
  const tokens = `${normOriginal(color)} ${normOriginal(material)}`;
  if (WARM_JEWELRY.some((w) => tokens.includes(w))) return "warm";
  if (COOL_JEWELRY.some((c) => tokens.includes(c))) return "cool";
  return "neutral";
}

// ─── Color Register Classification ───────────────────────────────────────────
//
// Classifies a clothing primary_color as neutral or accent.
// Neutral colors allow any jewelry tone — the color story is flexible.
// Accent colors create a dominant color signal — jewelry tone preference applies.
export function classifyColorRegister(
  primaryColor: string | null | undefined
): "neutral" | "accent" | "unknown" {
  if (!primaryColor) return "unknown";
  const c = primaryColor.toLowerCase().trim();
  const neutrals = ["black", "white", "ivory", "cream", "beige", "taupe", "gray", "grey", "navy"];
  if (neutrals.some((n) => c.includes(n))) return "neutral";
  return "accent";
}

// ─── Color Harmony Bonus ──────────────────────────────────────────────────────
//
// Soft scoring adjustment applied to jewelry candidates based on the clothing
// color register. Never a hard filter. Returns 0 when data is unavailable.
export function computeColorHarmonyBonus(
  jewelryTone: "warm" | "cool" | "neutral",
  clothingColorRegister: "neutral" | "accent" | "unknown"
): number {
  if (clothingColorRegister === "unknown") return 0;
  // Neutral clothing: any jewelry tone works — small positive to reward intentional choice
  if (clothingColorRegister === "neutral") return 0.3;
  // Accent clothing: reward tone echo (warm+warm, cool+cool), penalise strong competition
  if (clothingColorRegister === "accent") {
    if (jewelryTone === "neutral") return 0;
    return 0.3; // warm or cool both can work with accent clothing
  }
  return 0;
}

// ─── Occasion Match Bonus ─────────────────────────────────────────────────────
//
// Soft scoring adjustment: items whose occasion tag aligns with the current
// formality tier score slightly higher; clear mismatches score slightly lower.
// Returns 0 for null, empty, or ambiguous occasion values.
export function computeOccasionMatchBonus(
  itemOccasion: string | null | undefined,
  formalityTier: number
): number {
  const occ = (itemOccasion ?? "").toLowerCase().trim();
  if (!occ) return 0;

  const alignMap: Record<number, string[]> = {
    1: ["casual", "everyday", "weekend"],
    2: ["work", "business", "brunch", "church"],
    3: ["evening", "dinner", "cocktail", "date night"],
    4: ["formal", "wedding", "gala", "black tie"],
  };
  const conflictMap: Record<number, string[]> = {
    1: ["formal", "wedding", "gala", "black tie"],
    2: ["formal", "wedding", "gala"],
    3: ["casual", "everyday", "weekend"],
    4: ["casual", "everyday", "weekend"],
  };

  const aligned = alignMap[formalityTier] ?? [];
  const conflicting = conflictMap[formalityTier] ?? [];

  if (aligned.some((k) => occ.includes(k))) return 0.3;
  if (conflicting.some((k) => occ.includes(k))) return -0.2;
  return 0;
}

// ─── Archetype Match Bonus ────────────────────────────────────────────────────
//
// Additive per-item bonus that nudges the selection loop toward pieces the
// current user's style archetype naturally favors. Primary archetype at full
// ARCHETYPE_WEIGHT (2), secondary at half. The maximum primary contribution
// (2) stays strictly below FAVORITE_WEIGHT (3) so an inferred archetype
// signal never outweighs an explicit favorite flag in weightedPick's total.

export type ArchetypeMatchInput = {
  is_favorite?: boolean | null;
  created_at?: string | null;
  feelings?: string[] | null;
  last_surfaced_at?: string | null;
  name?: string | null;
  brand?: string | null;
  material?: string | null;
  notes?: string | null;
  occasion?: string | null;
  category?: string | null;
};

function archetypeMatchStrength(item: ArchetypeMatchInput, archetype: string, now: Date): number {
  const a = archetype.toLowerCase().replace("the ", "").trim();
  switch (a) {
    case "refined": {
      const ageInDays = item.created_at
        ? (now.getTime() - new Date(item.created_at).getTime()) / 86400000
        : 0;
      return item.is_favorite && ageInDays > 90 ? 1 : 0;
    }
    case "expressive":
      return (item.feelings?.length ?? 0) > 2 ? 1 : 0;
    case "fluent": {
      const daysSinceSurfaced = item.last_surfaced_at
        ? (now.getTime() - new Date(item.last_surfaced_at).getTime()) / 86400000
        : Infinity;
      return daysSinceSurfaced < 30 ? 1 : 0;
    }
    case "romantic": {
      const keywords = ["pearl", "lace", "floral", "vintage", "antique", "delicate", "enamel", "gold", "rose"];
      const name = (item.name ?? "").toLowerCase();
      return keywords.some((k) => name.includes(k)) ? 1 : 0;
    }
    case "curator": {
      const depth = (item.brand ? 1 : 0) + (item.material ? 1 : 0) + ((item.notes?.length ?? 0) > 10 ? 1 : 0);
      return depth >= 2 ? 1 : depth === 1 ? 0.5 : 0;
    }
    case "edge":
      return classifyIsStatementJewelry(item.category ?? null, item.name ?? null) ? 1 : 0;
    case "adventurer": {
      const practical = ["travel", "casual", "outdoor", "active", "work"];
      const occ = (item.occasion ?? "").toLowerCase();
      return practical.some((o) => occ.includes(o)) ? 1 : 0;
    }
    default:
      return 0;
  }
}

export function computeArchetypeBonus(
  item: ArchetypeMatchInput,
  primary: string | null,
  secondary: string | null,
  now: Date = new Date()
): number {
  const primaryBonus = primary ? ARCHETYPE_WEIGHT * archetypeMatchStrength(item, primary, now) : 0;
  const secondaryBonus = secondary ? ARCHETYPE_WEIGHT * 0.5 * archetypeMatchStrength(item, secondary, now) : 0;
  return primaryBonus + secondaryBonus;
}

const ARCHETYPE_DESCRIPTIONS: Record<string, string> = {
  refined: "a long-loved piece — reflects her Refined style",
  expressive: "rich with feeling — reflects her Expressive style",
  fluent: "an easy, frequent favorite — reflects her Fluent style",
  romantic: "soft, detailed, sentimental — reflects her Romantic style",
  curator: "considered down to the details — reflects her Curator style",
  edge: "bold and structural — reflects her Edge style",
  adventurer: "practical for how she actually lives — reflects her Adventurer style",
};

/**
 * Human-readable archetype match reason for decision_rationale, checking
 * primary then secondary. Returns null if neither matches — archetype
 * enrichment is additive, never forced into the narrative.
 */
export function describeArchetypeMatch(
  item: ArchetypeMatchInput,
  primary: string | null,
  secondary: string | null,
  now: Date = new Date()
): string | null {
  if (primary) {
    const a = primary.toLowerCase().replace("the ", "").trim();
    if (archetypeMatchStrength(item, primary, now) > 0 && ARCHETYPE_DESCRIPTIONS[a]) {
      return ARCHETYPE_DESCRIPTIONS[a];
    }
  }
  if (secondary) {
    const a = secondary.toLowerCase().replace("the ", "").trim();
    if (archetypeMatchStrength(item, secondary, now) > 0 && ARCHETYPE_DESCRIPTIONS[a]) {
      return ARCHETYPE_DESCRIPTIONS[a];
    }
  }
  return null;
}


// ─── Look-Level Coherence Score ───────────────────────────────────────────────
//
// Evaluates the engagement segment distribution of the assembled look.
// High: at least one Core, Workhorse, or Loved-but-underused piece present —
//       the look is anchored in the user's established wardrobe identity.
// Medium: at least one Dormant Opportunity piece — good rediscovery value,
//         but no established-identity anchor.
// Low: all Aspirational, Orphan, or Unclassified — the look may not feel
//      grounded in the user's actual style. Triggers a re-run if better
//      alternatives exist (collection size gate applied at call site).
export function computeLookCoherence(
  segments: EngagementSegment[]
): "high" | "medium" | "low" {
  const active = segments.filter(Boolean);
  if (active.length === 0) return "low";
  const highValue = ["Core", "Workhorse", "Loved but underused"] as const;
  if (active.some((s) => (highValue as readonly string[]).includes(s))) return "high";
  if (active.includes("Dormant Opportunity")) return "medium";
  return "low";
}

// ─── Texture Register Extractor ──────────────────────────────────────────────
//
// Reads texture_register from style_attributes. Returns null when the field
// is absent or not yet enriched (v:1 items). Null is never penalised —
// the veto and compatibility checks below skip items with missing data
// rather than guessing.
export type TextureRegister =
  | "soft"
  | "structured"
  | "casual"
  | "technical"
  | "ornamental"
  | "n_a";

export function extractTextureRegister(
  styleAttributes: { texture_register?: string | null } | null | undefined
): TextureRegister | null {
  const val = styleAttributes?.texture_register;
  if (!val) return null;
  const valid: TextureRegister[] = [
    "soft", "structured", "casual", "technical", "ornamental", "n_a",
  ];
  return (valid as string[]).includes(val) ? (val as TextureRegister) : null;
}

// ─── Texture Compatibility Bonus ─────────────────────────────────────────────
//
// Soft scoring modifier for completenessExtras clothing candidates.
// Rewards pairings where the candidate's texture register complements
// the anchor clothing rather than duplicating or clashing with it.
// Based on the "thin to thick" and "texture contrast creates interest"
// principles confirmed in independent stylist research.
//
// Returns a value between -0.3 and +0.3. Never a hard filter.
// Returns 0 when data is unavailable for either piece.
export function computeTextureCompatibilityBonus(
  candidateTexture: TextureRegister | null,
  anchorTexture: TextureRegister | null
): number {
  if (!candidateTexture || !anchorTexture) return 0;
  if (candidateTexture === "n_a" || anchorTexture === "n_a") return 0;

  // Same register on top of same register = bulk risk. Penalise.
  if (candidateTexture === anchorTexture) {
    // Exception: casual + casual is fine (cotton tee + denim jeans)
    if (candidateTexture === "casual") return 0;
    return -0.2;
  }

  // High-value pairings: structured + soft is the classic refined contrast.
  const goodPairs: Array<[TextureRegister, TextureRegister]> = [
    ["structured", "soft"],
    ["soft", "structured"],
    ["structured", "casual"],
    ["casual", "structured"],
  ];
  if (goodPairs.some(([a, b]) => a === anchorTexture && b === candidateTexture)) {
    return 0.2;
  }

  // Ornamental should not layer under/over structured outerwear.
  if (
    candidateTexture === "ornamental" &&
    (anchorTexture === "structured" || anchorTexture === "technical")
  ) {
    return -0.3;
  }

  return 0;
}

// ─── Three-Color Rule: Color Family Counter ───────────────────────────────────
//
// Counts distinct non-neutral, non-metal color families across all
// clothing and accessory items in the selected look. Jewelry metals
// (gold, silver, rose gold) are exempt per the empirically confirmed
// three-color rule — metals do not count toward the limit.
// Neutrals (black, white, ivory, cream, beige, taupe, gray, grey, navy)
// are also exempt — they are the foundation.
//
// Returns the count of distinct accent color families. The veto fires
// when this count exceeds 3.
const COLOR_NEUTRALS = new Set([
  "black", "white", "ivory", "cream", "beige", "taupe",
  "gray", "grey", "navy", "nude", "tan", "camel",
]);
const COLOR_METALS = new Set([
  "gold", "silver", "rose gold", "gunmetal", "bronze",
  "copper", "platinum", "metallic",
]);

export function countDistinctColorFamilies(
  colors: Array<string | null | undefined>
): number {
  const families = new Set<string>();
  for (const raw of colors) {
    if (!raw) continue;
    const c = raw.toLowerCase().trim();
    if (COLOR_NEUTRALS.has(c)) continue;
    if (COLOR_METALS.has(c)) continue;
    // Reduce to family: strip qualifiers like "light", "dark", "pale",
    // "deep", "bright", "dusty" to group light blue + dark blue as one family.
    const family = c
      .replace(/\b(light|dark|pale|deep|bright|dusty|soft|muted|warm|cool|pastel)\b/g, "")
      .trim();
    if (family) families.add(family);
  }
  return families.size;
}

// ─── Statement Accessory Classifier ──────────────────────────────────────────
//
// Identifies accessories that carry significant visual weight — pieces that
// act as a focal point in their own right and compete with statement jewelry.
// Used in the focal-point veto to detect cross-zone statement conflicts.
//
// Criteria: ornamental texture register (from style_attributes), or category-
// level signals (embellished bag, statement belt, oversized hat).
// Footwear is excluded — even striking shoes rarely compete with jewelry
// as a focal point in the necklace/earrings zone.
export function classifyIsStatementAccessory(
  accessoryRole: AccessoryRole | null | undefined,
  textureRegister: TextureRegister | null | undefined,
  name: string | null | undefined
): boolean {
  // Footwear is never a competing focal point in the jewelry zone
  if (accessoryRole === "footwear") return false;

  // Ornamental texture is the primary signal — sequins, beading,
  // metallic thread, embellishment make any accessory a focal point.
  if (textureRegister === "ornamental") return true;

  // Category-level signals independent of texture data
  const itemName = (name ?? "").toLowerCase();
  if (accessoryRole === "belt" && ["statement", "embellished", "jewelled", "crystal", "chain"].some((k) => itemName.includes(k))) return true;
  if (accessoryRole === "hat" && ["statement", "embellished", "feather", "wide brim"].some((k) => itemName.includes(k))) return true;
  if (accessoryRole === "bag" && ["embellished", "beaded", "sequin", "crystal", "jewel"].some((k) => itemName.includes(k))) return true;

  return false;
}

export type OutfitCandidate = PieceRef & {
  /**
   * Priority determines which candidate wins when two candidates compete
   * for the same structural slot. Higher wins.
   * 100 = algorithmic anchor (manual pick or auto-selected primary)
   *  80 = completeness extra (algorithm-chosen complement)
   *  50 = AI piecesSelected match
   *  30 = narrative text scan match (jewelry and accessory only — clothing
   *       is foundation-locked and is never sourced from narrative text)
   */
  priority: number;
  /** clothing_type field from the database row — used for role classification. */
  clothing_type?: string | null;
  /** style_attributes from the database row — layer_type is the primary role signal. */
  style_attributes?: {
    weather?: {
      layer_type?: string | null;
      warmth?: number | null;
      breathability?: number | null;
      rain_safe?: boolean | null;
    } | null;
    texture_register?: string | null;
  } | null;
  /**
   * Pre-computed accessory role for accessory candidates. When present,
   * composeOutfit uses this directly rather than re-classifying.
   */
  accessory_role?: AccessoryRole | null;
  /**
   * True when this candidate is an explicit anchor (manually picked by the
   * user OR auto-selected primary). Anchors must persist across Try Another
   * chains and are excluded from chain exclusion name walks.
   */
  is_anchor?: boolean;
};

export type ComposedOutfit = {
  selected: Array<PieceRef & { is_anchor?: boolean }>;
  overflow: Array<PieceRef & { is_anchor?: boolean }>;
  /** Items admitted via the generic "other" bucket for clothing or accessory
   * roles — meaning classifyGarmentRole/classifyAccessoryRole found no
   * keyword match. Legitimate for genuinely miscellaneous items (wallets,
   * keychains), but also the silent failure mode when a real
   * shoe/bottom/etc. has no category set and a name with no recognized
   * keyword — letting it bypass its real slot's cap. Surfaced for tracking,
   * not filtered — composeOutfit does not guess at a better classification. */
  unclassifiedFallbacks: Array<{ id: string; name: string; type: "clothing" | "accessory" }>;
};

export { composeOutfit } from "./style-engine/composition";
export { assessEligibility } from "./style-engine/eligibility";
export type { EligibilityAssessment, EligibilityContext } from "./style-engine/eligibility";
